import React, { useEffect, useState } from "react";
import Select from "react-select";
import "../styles/AddItemForm.css";
import "../styles/GRNFormRadio.css";
import "../styles/grnformvalidation.css";
import { GRN } from "../models/NewGrn";
import { getData } from "../api/ApiServices";
import { ApiResponse } from "../models/ItemModelTypes";
import { GrnUtils } from "../utils/grn/grnUtils";
import { RefreshCw } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import {AddGRNFormProps} from '../models/grnform/types'

const AddGRNForm: React.FC<AddGRNFormProps> = ({
    onSave,
    onCancel,
    suppliers,
    parts,
    isProcessing,
    isEditing,
    editingGRN,
    grnNo,
    prefilledData,
    isReadOnly
}) => {
    const { supplierOptions,
        setSupplierOptions,
        fetchSupplierOptions,
        supplierParts,
        setSupplierParts,
        fetchSupplierParts,
        fetchOnSearchPartDetails,
        purchaseNoOptions,
        fetchPoNumber,
        partNoOptions,
        fetchPartNumbers,
        fetchOnSearchPartNumbers
    } = GrnUtils();

    useEffect(() => {
        fetchSupplierOptions("");
        fetchPoNumber("");
    }, []);

    const initialState: GRN = {
        grnNo: "",
        grnDate: new Date().toISOString().split("T")[0],
        supplierId: "",
        poNo: "",
        number: "",
        date: "",
        partId: "",
        poQty: 0,
        balanceQty: 0,
        qty: 0,
        grnType: "WITHOUTPO", 
        invoiceType: "INVOICE",
        status: "ACTIVE",
    };

    const initialPartState = {
        partId: "",
        partName: "",
        model: "",
        category: "",
        uom: "",
        unitPrice: 0,
        packingQty: 0
    };

    const { readOnly } = prefilledData;
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [grnData, setGrnData] = useState<GRN>(initialState);
    const [partOptions, setPartOptions] = useState<any[]>([]);
    const [selectedSupplierCode, setSelectedSupplierCode] = useState<string>("");

    const permission = useSelector((state: RootState) => state.permissions)

    const [models, setModels] = useState<{ _id: string; name: string }[]>([]);
    const [categories, setCategories] = useState<{ _id: string; name: string }[]>([]);
    const [uoms, setUoms] = useState<{ _id: string; name: string }[]>([]);

    const [selectedPart, setSelectedPart] = useState(initialPartState);
    const [errors, setErrors] = useState({
        supplierId: "",
        partId: "",
        number: "",
        qty: "",
        grnType: "",
        invoiceType: ""
    });

    const [formSubmitted, setFormSubmitted] = useState(false);
    const handleRefresh = () => {
        window.location.reload();
    };

    // Function to find the supplier code from supplier ID
    const findSupplierCodeById = (supplierId:any) => {
        console.log(supplierOptions);
        
        const supplier = supplierOptions.find(s => s.value === supplierId);
        return supplier?.code || "";
    };

    useEffect(() => {
        if (prefilledData) {
            setGrnData(prev => ({
                ...prev,
                grnNo,
                supplierId: prefilledData.supplierId || "",
                number: prefilledData.number || "",
                date: prefilledData.date || "",
                grnType: prefilledData.grnType || "WITHOUTPO", 
                invoiceType: prefilledData.invoiceType || "INVOICE",
                poNo: prefilledData.poNo || ""
            }));

            if (prefilledData.supplierId) {
                setErrors(prev => ({
                    ...prev,
                    supplierId: ""
                }));
            }
        }
    }, [grnNo, prefilledData]);

    useEffect(() => {
        if (isEditing && editingGRN) {
            setGrnData({
                ...editingGRN,
                grnDate: formatDateForInput(editingGRN.grnDate || new Date().toISOString()),
                date: formatDateForInput(editingGRN.date),
                partId: editingGRN.partId?._id || editingGRN.partId
            });
            if (editingGRN.supplierId) {
                const supplierDetails = typeof editingGRN.supplierId === 'object' ?
                    editingGRN.supplierId : suppliers.find(s => s._id === editingGRN.supplierId);
                if (supplierDetails && supplierDetails.code) {
                    setSelectedSupplierCode(supplierDetails.code);
                }

                fetchSupplierParts(editingGRN.supplierId).then((response: any) => {
                    setSupplierParts(response);

                    const formattedPartOptions = response.map((p: any) => {
                        return {
                            value: p._id,
                            label: p.partDetails?.internalPartNo,
                            partName: p.partDetails?.partName,
                            unitPrice: p.unitPrice,
                            model: p.itemmodel.name,
                            category: p.itemcategory.name,
                            uom: p.uoms.name,
                            packingQty: p.partDetails?.packingQty || 0
                        };
                    });
                    console.log("formatted options: ", formattedPartOptions);
                    console.log("editing grn", editingGRN);

                    setPartOptions(formattedPartOptions);
                    const partId = editingGRN.partId?._id || editingGRN.partId;

                    const selectedPartDetails = formattedPartOptions.find((part: any) => part.value === partId);

                    console.log("selected part: ", selectedPartDetails);

                    if (selectedPartDetails) {
                        setSelectedPart({
                            partId: selectedPartDetails.value,
                            partName: selectedPartDetails.partName,
                            model: selectedPartDetails.model,
                            category: selectedPartDetails.category,
                            uom: selectedPartDetails.uom,
                            unitPrice: selectedPartDetails.unitPrice,
                            packingQty: selectedPartDetails.packingQty
                        });
                    }
                });
            }
        }
    }, [isEditing, editingGRN, models, categories, suppliers]);

    useEffect(() => {
        console.log("selected part: ", partOptions);
    }, [setPartOptions])

    const formatDateForInput = (dateString: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const numericFields = ["poQty", "balanceQty", "qty"];

        if (name === "qty") {
            if (value === "") {
                setGrnData((prev) => ({
                    ...prev,
                    [name]: "",
                }));

                setErrors(prev => ({
                    ...prev,
                    [name]: "Quantity is required"
                }));
            }
            else if (/^\d+$/.test(value) && value.length <= 5) {
                const newQty = Number(value);

                if (grnData.grnType === "WITHPO" && newQty > Number(grnData.poQty)) {
                    setErrors(prev => ({
                        ...prev,
                        [name]: "Quantity cannot exceed PO Quantity"
                    }));
                } else if (newQty <= 0) {
                    setErrors(prev => ({
                        ...prev,
                        [name]: "Quantity must be greater than 0"
                    }));
                } else {
                    setErrors(prev => ({
                        ...prev,
                        [name]: ""
                    }));
                }

                setGrnData((prev) => ({
                    ...prev,
                    [name]: newQty,
                }));
            }
        } else {
            setGrnData((prev) => ({
                ...prev,
                [name]: numericFields.includes(name) && value !== ""
                    ? Math.max(0, Number(value))
                    : value,
            }));

            if (name in errors) {
                setErrors(prev => ({
                    ...prev,
                    [name]: ""
                }));
            }
        }
    };

    const handleSelectChange = async (selectedOption: any, { name }: any) => {
        console.log("selected option: ", selectedOption);

        const selectedValue = selectedOption?.value || "";

        if (name === "poNo" && selectedOption) {
            setGrnData((prev) => ({
                ...prev,
                [name]: selectedValue,
                supplierId: selectedOption.supplierId || "",
            }));

            setSelectedSupplierCode(selectedOption.supplierCode || "");

            const response: any = await fetchPartNumbers(selectedValue);
            console.log("part response: ", response);

            setPartOptions(response);
        } else {
            setGrnData((prev) => ({
                ...prev,
                [name]: selectedValue,
            }));

            if (name in errors) {
                setErrors(prev => ({
                    ...prev,
                    [name]: ""
                }));
            }

            if (name === "supplierId" && selectedValue) {
                const selectedSupplier = supplierOptions.find(option => option.value === selectedValue);
                setSelectedSupplierCode(selectedSupplier?.label || "");

                try {
                    const response: any = await fetchSupplierParts(selectedValue);
                    setSupplierParts(response);
                    console.log("supplier part response: ", response);
                    const formattedPartOptions = response
                        .filter((p: any) => p.partDetails.status === "ACTIVE")
                        .map((p: any) => {

                            return {
                                value: p._id,
                                label: p.partDetails?.internalPartNo,
                                partName: p.partDetails?.partName,
                                unitPrice: p.unitPrice,
                                model: p.itemmodel.name,
                                category: p.itemcategory.name,
                                uom: p.uoms.name,
                                packingQty: p.partDetails?.packingQty || 0
                            };
                        });


                    setPartOptions(formattedPartOptions);
                    setGrnData(prev => ({
                        ...prev,
                        partId: "",
                        qty: 0
                    }));
                    setSelectedPart(initialPartState);
                } catch (error) {
                    console.error("Error fetching supplier parts:", error);
                }
            }
        }
    };

    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setGrnData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (name in errors) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }

        if (name === "grnType") {
            setGrnData(prev => ({
                ...prev,
                poNo: "",
                partId: "",
                qty: 0
            }));
            setSelectedPart(initialPartState);
            setSelectedSupplierCode("");
        }
    };

    const handlePartChange = (selectedOption: any) => {
        console.log("selected part options: ", selectedOption);

        if (grnData.grnType === "WITHPO") {
            setSelectedPart({
                partId: selectedOption.value,
                partName: selectedOption.partName,
                model: selectedOption.model,
                category: selectedOption.category,
                uom: selectedOption.uom,
                unitPrice: selectedOption.unitPrice,
                packingQty: selectedOption.packingQty || 0
            });
        }
        setGrnData((prev) => ({
            ...prev,
            partId: selectedOption.value,
            balanceQty:selectedOption.balanceQty
        }));

        const selectedPartData = partOptions.find(p => p.value === selectedOption.value);

        if (selectedPartData) {
            setSelectedPart({
                partId: selectedPartData.value,
                partName: selectedPartData.partName,
                model: selectedPartData.model,
                category: selectedPartData.category,
                uom: selectedPartData.uom,
                unitPrice: selectedPartData.unitPrice,
                packingQty: selectedPartData.packingQty || 0,
            });

            setGrnData((prev) => ({
                ...prev,
                poQty: selectedOption.poQty,                
            }))
        }
        setErrors(prev => ({
            ...prev,
            partId: ""
        }));
    };

    const validateForm = () => {
        const isWithPO = grnData.grnType === "WITHPO";
        const qtyValue = Number(grnData.qty);
        const poQtyValue = Number(grnData.poQty);

        const newErrors = {
            supplierId: !grnData.supplierId ? "Supplier is required" : "",
            partId: !grnData.partId ? "Part number is required" : "",
            number: !grnData.number ? `${getInvoiceLabels().numberLabel} is required` : "",
            qty: grnData.qty === "" ? "Quantity is required" :
                Number(grnData.qty) <= 0 ? "Quantity must be greater than 0" :
                    String(grnData.qty).length > 5 ? "Quantity must not exceed 5 digits" :
                        isWithPO && qtyValue > poQtyValue ? "Quantity cannot exceed PO Quantity" : "",
            grnType: !grnData.grnType ? "GRN Type is required" : "",
            invoiceType: !grnData.invoiceType ? "Invoice Type is required" : ""
        };

        setErrors(newErrors);
        return Object.values(newErrors).every(error => error === "");
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormSubmitted(true);

        if (!validateForm()) {
            return;
        }

        const formatDate = (date: string) => {
            return date && !isNaN(new Date(date).getTime())
                ? new Date(date).toISOString().split("Z")[0]
                : new Date().toISOString().split("Z")[0];
        };

        const finalGrnData = {
            ...grnData,
            grnDate: formatDate(grnData.grnDate),
            date: formatDate(grnData.date),
            qty: grnData.qty === "" ? 0 : Number(grnData.qty),
        };

        try {
            const response = await onSave(finalGrnData);

            if(response === null || response === undefined) {
                 throw new Error("Error submitting GRN form - same item cannot be created again");
            }
            if (isEditing) {
                handleCancel();
            } else {
                setGrnData(prev => ({
                    ...initialState,
                    grnNo: prev.grnNo,
                    grnDate: new Date().toISOString().split("T")[0],
                    supplierId: prev.supplierId,
                    number: prev.number,
                    date: prev.date,
                    partId: "",
                    qty: 0
                }));

                setSelectedPart(initialPartState);

                setFormSubmitted(false);
            }
        } catch (error) {
            console.error("Error submitting GRN form:", error);
            setGrnData(prev => ({
                ...prev,
                partId: "",
                qty: 0
            }));

            setSelectedPart(initialPartState);

            setFormSubmitted(false);
        }
    };

    const handleCancel = () => {
        setGrnData({
            ...initialState,
            grnNo: grnNo || "",
            grnDate: new Date().toISOString().split("T")[0],
            supplierId: prefilledData.supplierId || "",
            number: prefilledData.number || "",
            date: prefilledData.date || ""
        });

        setSelectedPart(initialPartState);
        // setSelectedSupplierCode("");

        setErrors({
            supplierId: "",
            partId: "",
            number: "",
            qty: "",
            grnType: "",
            invoiceType: ""
        });

        setFormSubmitted(false);
        onCancel();
    };

    const getInvoiceLabels = () => {
        return grnData.invoiceType === "DC"
            ? { numberLabel: "DC No", dateLabel: "DC Date" }
            : { numberLabel: "Invoice No", dateLabel: "Invoice Date" };
    };



    const displaySupplierOption = (grnData.supplierId && selectedSupplierCode
        ? { value: grnData.supplierId, label: selectedSupplierCode }
        : null);

    const today = new Date().toISOString().split("T")[0];

    return (
        <>
            <div className="item-models-header">
                <div className="border grn-header">
                    <div className="start-end-layout">
                        <div className="breadcrumb">
                            <span className="breadcrumb-current" onClick={onCancel}>
                                GRN
                            </span>
                            <span className="breadcrumb-separator"> &gt; </span>
                            <span className="breadcrumb-item">{isEditing ? "Edit GRN" : "New GRN"}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-container grn-form">
                <form onSubmit={handleSubmit} className="add-item-form">
                    <div className="radio-row-container">
                        <div className="form-group2 radio-container">
                            <label>GRN Type:</label>
                            <div className="radio-group">
                                <label>
                                    <input
                                        type="radio"
                                        name="grnType"
                                        value="WITHOUTPO"
                                        checked={grnData.grnType === "WITHOUTPO"}
                                        onChange={handleRadioChange}
                                        disabled={isEditing || readOnly}
                                    />
                                    WITHOUTPO
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="grnType"
                                        value="WITHPO"
                                        checked={grnData.grnType === "WITHPO"}
                                        onChange={handleRadioChange}
                                        disabled={isEditing || readOnly}
                                    />
                                    WITHPO
                                </label>
                            </div>
                            {formSubmitted && errors.grnType && (
                                <div className="error-message-grn">{errors.grnType}</div>
                            )}
                        </div>
                        <div className="form-group2 radio-container">
                            <label>Invoice Type:</label>
                            <div className="radio-group">
                                <label>
                                    <input
                                        type="radio"
                                        name="invoiceType"
                                        value="INVOICE"
                                        checked={grnData.invoiceType === "INVOICE"}
                                        onChange={handleRadioChange}
                                        disabled={isEditing || readOnly}
                                    />
                                    INVOICE
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="invoiceType"
                                        value="DC"
                                        checked={grnData.invoiceType === "DC"}
                                        onChange={handleRadioChange}
                                        disabled={isEditing || readOnly}
                                    />
                                    DC
                                </label>
                            </div>
                            {formSubmitted && errors.invoiceType && (
                                <div className="error-message-grn">{errors.invoiceType}</div>
                            )}
                        </div>
                        <div className="form-group2 refresh-container">
                            <button
                                type="button"
                                className="refresh-button"
                                onClick={handleRefresh}
                                title="Refresh"
                            >
                                <RefreshCw size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="form-grid">
                        <div className="form-group">
                            <label>GRN No:</label>
                            <div className="input-error-container">
                                <input type="text" name="grnNo" value={grnNo || ""} readOnly className="read-only-input" />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>GRN Date:</label>
                            <div className="input-error-container">
                                <input
                                    type="date"
                                    name="grnDate"
                                    value={grnData.grnDate || today}
                                    readOnly
                                    className="read-only-input"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Supplier Code:</label>
                            <div className="input-error-container">
                                <Select
                                    name="supplierId"
                                    options={supplierOptions}
                                    value={displaySupplierOption}
                                    onChange={(selectedOption) => handleSelectChange(selectedOption, { name: "supplierId" })}
                                    onInputChange={(inputValue) => {
                                        setSearchTerm(inputValue);
                                        fetchSupplierOptions(inputValue);
                                    }}
                                    placeholder="Select Supplier"
                                    className={`react-select-container ${formSubmitted && errors.supplierId ? "error-select" : ""}`}
                                    classNamePrefix="react-select"
                                    isDisabled={isEditing || isReadOnly || grnData.grnType === "WITHPO"}
                                />
                                {formSubmitted && errors.supplierId && (
                                    <div className="error-messagegrn">{errors.supplierId}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>PO No:</label>
                            <div className="input-error-container">
                                {grnData.grnType === "WITHPO" ? (
                                    <Select
                                        name="poNo"
                                        options={purchaseNoOptions}
                                        value={purchaseNoOptions.find((o) => o.value === grnData.poNo) || null}
                                        onChange={(selectedOption) => handleSelectChange(selectedOption, { name: "poNo" })}
                                        onInputChange={(inputValue) => {
                                            fetchPoNumber(inputValue);
                                        }}
                                        placeholder="Select PO Number"
                                        className={`react-select-container`}
                                        classNamePrefix="react-select"
                                        isDisabled={isEditing || isReadOnly}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        name="poNo"
                                        value={grnData.poNo}
                                        readOnly
                                        className="read-only-input"
                                    />
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{getInvoiceLabels().numberLabel}:</label>
                            <div className="input-error-container">
                                <input
                                    type="text"
                                    name="number"
                                    value={grnData.number}
                                    onChange={handleChange}
                                    disabled={isEditing || isReadOnly}
                                    className={formSubmitted && errors.number ? "error-input" : ""}
                                />
                                {formSubmitted && errors.number && (
                                    <div className="error-messagegrn">{errors.number}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>{getInvoiceLabels().dateLabel}:</label>
                            <div className="input-error-container">
                                <input
                                    type="date"
                                    name="date"
                                    value={grnData.date || today}
                                    onChange={handleChange}
                                    disabled={isEditing || isReadOnly}
                                    onKeyDown={(e) => e.preventDefault()}
                                    max={new Date().toISOString().split("T")[0]}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Part No:</label>
                            <div className="input-error-container">
                                <Select
                                    name="partId"
                                    options={partOptions}
                                    value={partOptions.find((o) => o.value === grnData.partId) || null}
                                    onChange={handlePartChange}
                                    onInputChange={(inputValue) => {
                                        setSearchTerm(inputValue);

                                        if (grnData.grnType === "WITHPO") {
                                            fetchOnSearchPartNumbers(inputValue, grnData.poNo || "").then((data) => setPartOptions(data));
                                        } else {
                                            fetchOnSearchPartDetails(inputValue);
                                        }
                                    }}
                                    placeholder="Select Part No"
                                    className={`react-select-container ${formSubmitted && errors.partId ? "error-select" : ""}`}
                                    classNamePrefix="react-select"
                                    isDisabled={isReadOnly && isEditing}
                                />
                                {formSubmitted && errors.partId && (
                                    <div className="error-messagegrn">{errors.partId}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Part Name:</label>
                            <div className="input-error-container">
                                <input type="text" value={selectedPart.partName} readOnly placeholder="Selected Part Name" className="read-only-input" />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Unit Price:</label>
                            <div className="input-error-container">
                                <input type="text" value={selectedPart.unitPrice} readOnly placeholder="Unit Price" className="read-only-input" />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Model:</label>
                            <div className="input-error-container">
                                <input type="text" value={selectedPart.model} readOnly placeholder="Model" className="read-only-input" />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Category:</label>
                            <div className="input-error-container">
                                <input type="text" value={selectedPart.category} readOnly placeholder="Category" className="read-only-input" />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Packing Quantity:</label>
                            <div className="input-error-container">
                                <input type="text" value={selectedPart.packingQty || ""} readOnly placeholder="Packing Quantity" className="read-only-input" />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>PO Quantity:</label>
                            <div className="input-error-container">
                                <input type="number" name="poQty" value={grnData.poQty} readOnly className="read-only-input" />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Balance Quantity:</label>
                            <div className="input-error-container">
                                <input type="number" name="balanceQty" value={grnData.balanceQty} readOnly className="read-only-input" />
                            </div>
                        </div>


                        <div className="form-group">
                            <label>{`Quantity(${selectedPart.uom || ''}):`}</label>
                            <div className="input-error-container">
                                <input
                                    type="text"
                                    name="qty"
                                    value={grnData.qty}
                                    onChange={handleChange}
                                    className={errors.qty ? "error-input" : ""}
                                />
                                {errors.qty && (
                                    <div className="error-messagegrn">{errors.qty}</div>
                                )}
                            </div>
                        </div>
                    </div>
                    {permission.menus.grns.create ?
                        <div className="button-container">
                            {isEditing && (
                                <button type="button" className="button cancel-button" onClick={handleCancel}>
                                    Cancel
                                </button>
                            )}
                            <button type="submit" className="button save-button" disabled={isProcessing}>
                                {isProcessing ? "Saving..." : isEditing ? "Update GRN" : "Add GRN"}
                            </button>
                        </div>
                        : <></>}
                </form>
            </div>
        </>
    );
};

export default AddGRNForm;